"use client";

import { useMemo, useState } from "react";
import {
  CodeBlock,
  CodeBlockContainer,
  CodeBlockHeader,
  type CustomRendererProps,
} from "streamdown";
import {
  FencedCodeCopyDownload,
  WrapCodeButton,
  fencedCodeActionButtonClassName,
  useFencedCodeBlockOptions,
  wrapCodeClassName,
} from "./fenced-code-chrome";
import { JsonValueTable, JsonViewToggle, parseJson } from "./json-table";

function JsonFormatToggle({ pretty, onToggle }: { pretty: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={pretty}
      className={fencedCodeActionButtonClassName}
    >
      {pretty ? "Original" : "Pretty"}
    </button>
  );
}

/** Keeps the JSON source as authored, with an optional table projection. */
export function JsonCodeBlock({ code, isIncomplete, meta }: CustomRendererProps) {
  const [view, setView] = useState<"code" | "table">("code");
  const [wrap, setWrap] = useState(false);
  const [pretty, setPretty] = useState(false);
  const parsed = useMemo(() => parseJson(code), [code]);
  const renderedCode = useMemo(
    () => (pretty && parsed !== null ? JSON.stringify(parsed, null, 2) : code),
    [code, parsed, pretty],
  );
  const { lineNumbers, startLine, copy, download } = useFencedCodeBlockOptions(meta);
  const toggleView = () => setView((current) => (current === "code" ? "table" : "code"));
  const wrapClass = wrapCodeClassName(wrap);
  const actions = (
    <>
      {parsed !== null && (
        <JsonViewToggle
          view={view}
          onToggle={toggleView}
          className={fencedCodeActionButtonClassName}
        />
      )}
      {view === "code" && (
        <>
          {parsed !== null && (
            <JsonFormatToggle pretty={pretty} onToggle={() => setPretty((current) => !current)} />
          )}
          <WrapCodeButton wrap={wrap} onClick={() => setWrap((current) => !current)} />
        </>
      )}
      <FencedCodeCopyDownload code={renderedCode} language="json" copy={copy} download={download} />
    </>
  );

  if (view === "table" && parsed !== null) {
    return (
      <CodeBlockContainer language="json" isIncomplete={isIncomplete}>
        <CodeBlockHeader language="json" />
        <div className="pointer-events-none sticky top-2 z-10 -mt-10 flex h-8 items-center justify-end">
          <div
            className="pointer-events-auto flex shrink-0 items-center gap-2 rounded-md border border-sidebar bg-sidebar/80 px-1.5 py-1 supports-[backdrop-filter]:bg-sidebar/70 supports-[backdrop-filter]:backdrop-blur"
            data-streamdown="code-block-actions"
          >
            {actions}
          </div>
        </div>
        <div className="overflow-x-auto rounded-md border border-border bg-background">
          <JsonValueTable value={parsed} />
        </div>
      </CodeBlockContainer>
    );
  }

  return (
    <CodeBlock
      key={renderedCode}
      code={renderedCode}
      language="json"
      isIncomplete={isIncomplete}
      lineNumbers={lineNumbers}
      {...(startLine === undefined ? {} : { startLine })}
      {...(wrapClass === undefined ? {} : { className: wrapClass })}
      data-testid="json-source"
    >
      {actions}
    </CodeBlock>
  );
}
